import React, { useEffect, useMemo, useState } from "react";
import PublicLayout from "../components/PublicLayout";
import PropertyCard from "../components/PropertyCard";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/api";

export default function Properties() {
    const { t } = useI18n();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ transaction: "", type: "", city: "" });

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (filters.transaction) params.transaction = filters.transaction;
        if (filters.type) params.type = filters.type;
        if (filters.city) params.city = filters.city;
        api.get("/properties", { params })
            .then((r) => setList(r.data))
            .catch(() => setList([]))
            .finally(() => setLoading(false));
    }, [filters]);

    const cities = useMemo(() => {
        const set = new Set(list.map((p) => p.city).filter(Boolean));
        return Array.from(set);
    }, [list]);

    const reset = () => setFilters({ transaction: "", type: "", city: "" });

    return (
        <PublicLayout>
            <section className="bg-brand-navy text-white" data-testid="properties-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                    <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                        {t("nav.properties")}
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl">{t("properties.title")}</h1>
                    <p className="mt-4 text-white/75 max-w-2xl">{t("properties.subtitle")}</p>
                </div>
            </section>

            <section className="bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-end gap-4" data-testid="properties-filters">
                    <FilterSelect
                        label={t("properties.filters.transaction")}
                        value={filters.transaction}
                        testid="filter-transaction"
                        onChange={(v) => setFilters((f) => ({ ...f, transaction: v }))}
                        options={[
                            { value: "", label: t("properties.filters.all") },
                            { value: "sale", label: t("properties.filters.sale") },
                            { value: "rent", label: t("properties.filters.rent") },
                        ]}
                    />
                    <FilterSelect
                        label={t("properties.filters.type")}
                        value={filters.type}
                        testid="filter-type"
                        onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
                        options={[
                            { value: "", label: t("properties.filters.all") },
                            { value: "terrain", label: t("properties.filters.terrain") },
                            { value: "villa", label: t("properties.filters.villa") },
                            { value: "appartement", label: t("properties.filters.appartement") },
                            { value: "maison", label: t("properties.filters.maison") },
                        ]}
                    />
                    <FilterSelect
                        label={t("properties.filters.city")}
                        value={filters.city}
                        testid="filter-city"
                        onChange={(v) => setFilters((f) => ({ ...f, city: v }))}
                        options={[
                            { value: "", label: t("properties.filters.all") },
                            ...cities.map((c) => ({ value: c, label: c })),
                        ]}
                    />
                    <button
                        onClick={reset}
                        data-testid="filter-reset"
                        className="ml-auto px-5 py-3 text-[0.7rem] tracking-[0.22em] uppercase font-semibold text-brand-navy hover:text-brand-gold transition-colors"
                    >
                        {t("properties.filters.reset")}
                    </button>
                </div>
            </section>

            <section className="py-16 bg-brand-paper min-h-[40vh]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="text-center text-gray-500 py-20" data-testid="properties-loading">Chargement...</div>
                    ) : list.length === 0 ? (
                        <div className="text-center text-gray-500 py-20" data-testid="properties-empty">{t("properties.empty")}</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
                            {list.map((p) => (
                                <PropertyCard key={p.id} p={p} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}

function FilterSelect({ label, value, options, onChange, testid }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy">
                {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="bg-white border border-gray-200 px-4 py-2.5 text-sm text-brand-navy focus:outline-none focus:border-brand-gold transition-colors min-w-[160px]"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </label>
    );
}
